import { useEffect, useMemo, useState } from 'react';
import { addItem, removeItem, setQuantity, setLineNote, basketTotals, formatPounds, toOrderPayload, reconcileBasket } from './basket.js';
import { noteSessionEnded } from './sessionEnded.js';

// Client ordering portal, wired to the live API. Served on
// portal.altonvalley.com (or /portal locally), separate from the staff app.
// Sign-in is the client-contact account (/api/client/login); everything else
// is /api/portal/*, which serves sell prices only — cost, margin and supplier
// never reach this surface, and the server prices every order line itself.

const ORDER_STATUS_STEPS = ['submitted', 'accepted', 'ordered', 'delivered'];
const ORDER_STATUS_LABELS = {
  submitted: 'Submitted', accepted: 'Accepted', ordered: 'Ordered',
  delivered: 'Delivered', cancelled: 'Cancelled',
};

const STATUS_PILL = {
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  accepted:  'bg-amber-50 text-amber-700 border-amber-200',
  ordered:   'bg-violet-50 text-violet-700 border-violet-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

async function portalFetch(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    noteSessionEnded(res.status, data);
    const err = new Error(data?.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Same key the staff app uses, so the preference follows the browser.
function StatusPill({ status }) {
  return (
    <span className={`inline-block border rounded-full px-2.5 py-0.5 text-[10px] font-bold ${STATUS_PILL[status] || STATUS_PILL.submitted}`}>
      {ORDER_STATUS_LABELS[status] || status}
    </span>
  );
}

function GoldHeading({ first, second }) {
  return (
    <h1 className="text-2xl font-extrabold text-navy">
      {first} <span className="text-gold">{second}</span>
    </h1>
  );
}

// Prices are live, so a basket can go stale between browsing and paying. The
// client is told what moved, in the basket, before they submit.
function PriceChangeNote({ notice, onDismiss }) {
  if (!notice || (!notice.changes.length && !notice.removed.length)) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2.5 text-[12px] mb-4">
      <div className="font-bold mb-1">Some prices have changed since you added these</div>
      {notice.changes.map(c => (
        <div key={c.sku}>
          {c.name}: {formatPounds(c.was)} is now <b>{formatPounds(c.now)}</b>
        </div>
      ))}
      {notice.removed.map(r => (
        <div key={r.sku}>{r.name} is no longer available and has been removed.</div>
      ))}
      <button onClick={onDismiss} className="mt-1.5 underline text-[11px]">Got it</button>
    </div>
  );
}

function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-[12px] mb-4">
      {children}
    </div>
  );
}

function QuantityStepper({ value, onChange }) {
  const btn = 'w-7 h-7 border border-gray-300 bg-white rounded-md text-[13px] text-gray-600 leading-none hover:bg-gray-50';
  return (
    <div className="inline-flex items-center gap-1.5">
      <button className={btn} onClick={() => onChange(value - 1)} aria-label="Reduce quantity">−</button>
      <span className="min-w-[22px] text-center text-[13px] font-bold text-navy">{value}</span>
      <button className={btn} onClick={() => onChange(value + 1)} aria-label="Increase quantity">+</button>
    </div>
  );
}

function CatalogueView({ products, loading, error, basket, setBasket }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter(p =>
      (category === 'All' || p.category === category) &&
      (!q || [p.display_name, p.brand, p.description, p.sku].some(v => (v || '').toLowerCase().includes(q)))
    );
  }, [products, search, category]);

  const inBasket = (sku) => basket.find(l => l.sku === sku);

  return (
    <div>
      <GoldHeading first="Order" second="catalogue" />
      <p className="text-xs text-gray-500 mt-1.5 mb-5 max-w-xl">
        Browse the product range and build an order. Prices shown are your prices;
        Alton Valley reviews and places every order with the distributor.
      </p>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex flex-wrap items-center gap-2.5 mb-5">
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by product, vendor or SKU…"
          className="form-input flex-1 min-w-[240px]" />
        <div className="flex flex-wrap gap-1.5">
          {categories.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`border rounded-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${category === c ? 'bg-gold border-gold text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {visible.map(p => {
          const line = inBasket(p.sku);
          return (
            <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
              {p.brand && <div className="text-[9px] font-bold text-gold uppercase tracking-widest">{p.brand}</div>}
              <div className="text-[13px] font-bold text-navy leading-snug">{p.display_name}</div>
              <div className="text-[9px] text-gray-400">SKU: {p.sku}{p.category ? ` · ${p.category}` : ''}</div>
              <div className="text-[11px] text-gray-500 leading-relaxed flex-1">{p.description}</div>
              <div className="flex items-baseline justify-between">
                <span className="text-[17px] font-extrabold text-navy">{formatPounds(p.price_pence)}
                  <span className="text-[9px] font-semibold text-gray-400 ml-1">ex VAT</span>
                </span>
              </div>
              {line ? (
                <div className="flex items-center justify-between">
                  <QuantityStepper value={line.quantity} onChange={(q) => setBasket(b => setQuantity(b, p.sku, q))} />
                  <span className="text-[10px] text-gray-500">in basket</span>
                </div>
              ) : (
                <button onClick={() => setBasket(b => addItem(b, { id: p.id, sku: p.sku, name: p.display_name, price_pence: p.price_pence }))}
                  className="btn-primary rounded-md py-2 text-[11px] font-bold">
                  Add to basket
                </button>
              )}
            </div>
          );
        })}
        {!loading && visible.length === 0 && !error && (
          <div className="col-span-full text-center py-10 text-gray-400 text-xs">
            {products.length === 0 ? 'The catalogue is empty right now. Check back soon.' : 'Nothing in the catalogue matches that search.'}
          </div>
        )}
        {loading && <div className="col-span-full text-center py-10 text-gray-400 text-xs">Loading the catalogue…</div>}
      </div>
    </div>
  );
}

function BasketView({ basket, setBasket, onSubmitted, goToCatalogue, notice, setNotice }) {
  const [clientRef, setClientRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const totals = basketTotals(basket);
  const vat = Math.round(totals.subtotal_pence * 0.2);

  if (basket.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-sm font-bold text-navy mb-1.5">Your basket is empty</div>
        <div className="text-[11px] text-gray-400 mb-5">Add items from the catalogue to build an order.</div>
        <button onClick={goToCatalogue} className="btn-primary rounded-md px-5 py-2.5 text-[11px] font-bold">
          Browse the catalogue
        </button>
      </div>
    );
  }

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const order = await portalFetch('portal/orders', { method: 'POST', body: toOrderPayload(basket, clientRef, notes) });
      onSubmitted(order);
    } catch (err) {
      // A price moved between opening the basket and pressing submit. Take the
      // server's figures, show what changed, and let them decide again.
      if (err.status === 409 && Array.isArray(err.data?.changed)) {
        const changes = err.data.changed.map(c => {
          const line = basket.find(l => l.id === c.portal_product_id);
          return { sku: line?.sku || c.portal_product_id, name: c.display_name, was: c.was_pence, now: c.now_pence };
        });
        setBasket(b => b.map(l => {
          const c = err.data.changed.find(x => x.portal_product_id === l.id);
          return c ? { ...l, price_pence: c.now_pence } : l;
        }));
        setNotice({ changes, removed: [] });
        setError('');
      } else {
        setError(err.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div>
      <GoldHeading first="Your" second="order" />
      <p className="text-xs text-gray-500 mt-1.5 mb-5 max-w-xl">
        Review your selection, add any notes, then submit. Alton Valley reviews
        every order before placing it with the distributor.
      </p>
      <ErrorNote>{error}</ErrorNote>
      <PriceChangeNote notice={notice} onDismiss={() => setNotice(null)} />
      <div className="grid gap-4 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 300px' }}>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
          {basket.map(l => (
            <div key={l.sku} className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-navy">{l.name}</div>
                  <div className="text-[9px] text-gray-400">{l.sku} · {formatPounds(l.price_pence)} each ex VAT</div>
                </div>
                <QuantityStepper value={l.quantity} onChange={(q) => setBasket(b => setQuantity(b, l.sku, q))} />
                <div className="w-20 text-right text-xs font-bold text-navy">{formatPounds(l.price_pence * l.quantity)}</div>
                <button onClick={() => setBasket(b => removeItem(b, l.sku))}
                  className="text-gray-400 hover:text-gray-600 text-[11px]" aria-label={`Remove ${l.name}`}>✕</button>
              </div>
              <input
                value={l.note || ''} onChange={(e) => setBasket(b => setLineNote(b, l.sku, e.target.value))}
                placeholder="Line note (optional)"
                className="form-input w-full mt-2.5 text-[11px]" />
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-xs font-extrabold text-navy mb-3.5">Order summary</div>
          {[
            ['Items', String(totals.item_count)],
            ['Subtotal (ex VAT)', formatPounds(totals.subtotal_pence)],
            ['VAT (20%)', formatPounds(vat)],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between text-[11px] text-gray-500 mb-2">
              <span>{k}</span><span>{v}</span>
            </div>
          ))}
          <div className="flex justify-between text-[13px] font-extrabold text-navy border-t border-gray-100 pt-2.5 mb-4">
            <span>Total</span><span>{formatPounds(totals.subtotal_pence + vat)}</span>
          </div>
          <label className="block text-[10px] font-semibold text-gray-700 mb-1">Your reference (optional)</label>
          <input value={clientRef} onChange={(e) => setClientRef(e.target.value)} placeholder="e.g. your PO number"
            className="form-input w-full mb-3 text-[11px]" />
          <label className="block text-[10px] font-semibold text-gray-700 mb-1">Order notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Delivery requirements, deadlines, anything else we should know…"
            className="form-textarea w-full mb-3.5 text-[11px]" />
          <button onClick={submit} disabled={busy}
            className="w-full bg-gold hover:opacity-90 text-white rounded-md py-2.5 text-xs font-extrabold disabled:opacity-60">
            {busy ? 'Submitting…' : 'Submit order'}
          </button>
          <p className="text-[9px] text-gray-400 mt-2.5 leading-relaxed">
            Your order is submitted to Alton Valley for review and placement.
            Nothing is sent to a distributor directly.
          </p>
        </div>
      </div>
    </div>
  );
}

function ConfirmationView({ order, goToOrders, goToCatalogue }) {
  return (
    <div className="text-center py-12">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-2xl leading-[3.5rem]">✓</div>
      <div className="text-base font-extrabold text-navy mb-1.5">Order {order.reference} submitted</div>
      <div className="text-[11px] text-gray-500 max-w-sm mx-auto mb-6 leading-relaxed">
        The Alton Valley team will review your order and place it with our suppliers.
        You can follow its progress on the orders page.
      </div>
      <div className="flex gap-2.5 justify-center">
        <button onClick={goToOrders} className="btn-primary rounded-md px-5 py-2.5 text-[11px] font-bold">
          View orders
        </button>
        <button onClick={goToCatalogue} className="btn-secondary border rounded-md px-5 py-2.5 text-[11px] font-bold">
          Back to catalogue
        </button>
      </div>
    </div>
  );
}

function StatusTimeline({ status }) {
  if (status === 'cancelled') return null;
  const reached = ORDER_STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center mt-3 mb-1">
      {ORDER_STATUS_STEPS.map((step, i) => (
        <div key={step} className={`flex items-center ${i < ORDER_STATUS_STEPS.length - 1 ? 'flex-1' : ''}`}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-2.5 h-2.5 rounded-full ${i <= reached ? 'bg-gold' : 'bg-gray-200'}`} />
            <div className={`text-[8px] font-semibold whitespace-nowrap ${i <= reached ? 'text-gold' : 'text-gray-400'}`}>{ORDER_STATUS_LABELS[step]}</div>
          </div>
          {i < ORDER_STATUS_STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1.5 mb-3.5 ${i < reached ? 'bg-gold' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function OrdersView({ orders, loading, error }) {
  const [open, setOpen] = useState(null);
  return (
    <div>
      <GoldHeading first="My" second="orders" />
      <p className="text-xs text-gray-500 mt-1.5 mb-5 max-w-xl">
        Your submitted orders. Statuses are updated by Alton Valley as each order
        moves from submitted through to delivered.
      </p>
      <ErrorNote>{error}</ErrorNote>
      {loading && <div className="text-center py-10 text-gray-400 text-xs">Loading your orders…</div>}
      {!loading && orders.length === 0 && !error && (
        <div className="border border-dashed border-gray-300 rounded-xl text-center py-14 text-gray-400 text-xs">
          You have not placed any orders yet.
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        {orders.map(o => {
          const isOpen = open === o.reference;
          return (
            <div key={o.reference} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button onClick={() => setOpen(isOpen ? null : o.reference)}
                className="w-full flex items-center gap-3.5 p-4 text-left">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-extrabold text-navy">
                    {o.reference}
                    {o.client_reference && <span className="text-[10px] font-semibold text-gray-400 ml-2">{o.client_reference}</span>}
                  </div>
                  <div className="text-[9px] text-gray-400 mt-0.5">Submitted {(o.submitted_at || '').slice(0, 10)} · {o.lines.length} line{o.lines.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="text-xs font-bold text-navy">{formatPounds(o.subtotal_pence)}
                  <span className="text-[8px] font-semibold text-gray-400 ml-1">ex VAT</span>
                </div>
                <StatusPill status={o.status} />
                <span className="text-gray-400 text-[10px]">{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 px-4 pt-2.5 pb-4">
                  <StatusTimeline status={o.status} />
                  {o.lines.map((l, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-gray-500 py-1.5 border-b border-gray-100 last:border-0">
                      <span>{l.quantity} × {l.display_name}{l.note ? <span className="text-gray-400"> — “{l.note}”</span> : null}</span>
                      <span className="font-bold text-navy">{formatPounds(l.line_total_pence)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The ordering section of the client portal.
//
// It owns its own tabs and nothing else: sign-in, the header, dark mode and
// switching between sections all belong to the shell around it, because they
// are the same whichever section a client is in.
export default function OrderingSection() {
  const [view, setView] = useState('catalogue');
  const [basket, setBasket] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalogueState, setCatalogueState] = useState({ loading: true, error: '' });
  const [orders, setOrders] = useState([]);
  const [ordersState, setOrdersState] = useState({ loading: true, error: '' });
  const [lastOrder, setLastOrder] = useState(null);
  const [basketNotice, setBasketNotice] = useState(null);

  useEffect(() => {
    portalFetch('portal/catalogue')
      .then(r => { setProducts(r.products); setCatalogueState({ loading: false, error: '' }); })
      .catch(e => setCatalogueState({ loading: false, error: e.message }));
    portalFetch('portal/orders')
      .then(r => { setOrders(r.orders); setOrdersState({ loading: false, error: '' }); })
      .catch(e => setOrdersState({ loading: false, error: e.message }));
  }, []);

  // Opening the basket re-prices it against the catalogue as it stands, so a
  // client sees a change while they can still act on it rather than having the
  // order refused when they press submit.
  const openBasket = async () => {
    setView('basket');
    try {
      const { products: current } = await portalFetch('portal/catalogue');
      setProducts(current);
      const { lines, changes, removed } = reconcileBasket(basket, current);
      if (changes.length || removed.length) {
        setBasket(lines);
        setBasketNotice({ changes, removed });
      }
    } catch { /* keep the basket as it stands; submitting still checks */ }
  };

  const submitted = (order) => {
    setLastOrder(order);
    setBasket([]);
    setBasketNotice(null);
    setView('confirmation');
    portalFetch('portal/orders').then(r => setOrders(r.orders)).catch(() => {});
  };

  const totals = basketTotals(basket);
  const tabs = [
    { id: 'catalogue', label: 'Catalogue' },
    { id: 'orders', label: 'My orders' },
    { id: 'basket', label: 'Basket', badge: totals.item_count },
  ];
  const active = view === 'confirmation' ? 'orders' : view;

  return (
    <div>
      <div className="flex gap-1.5 mb-5">
        {tabs.map(t => (
          <button key={t.id} onClick={() => (t.id === 'basket' ? openBasket() : setView(t.id))}
            className={`border rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
              active === t.id ? 'bg-navy border-navy text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {t.label}
            {t.badge > 0 && (
              <span className={`text-[8px] font-bold rounded-full px-1.5 py-px ${active === t.id ? 'bg-white text-navy' : 'bg-gold text-white'}`}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {view === 'catalogue' && (
        <CatalogueView products={products} loading={catalogueState.loading} error={catalogueState.error}
          basket={basket} setBasket={setBasket} />
      )}
      {view === 'basket' && (
        <BasketView basket={basket} setBasket={setBasket} onSubmitted={submitted}
          goToCatalogue={() => setView('catalogue')}
          notice={basketNotice} setNotice={setBasketNotice} />
      )}
      {view === 'confirmation' && lastOrder && (
        <ConfirmationView order={lastOrder} goToOrders={() => setView('orders')} goToCatalogue={() => setView('catalogue')} />
      )}
      {view === 'orders' && <OrdersView orders={orders} loading={ordersState.loading} error={ordersState.error} />}
    </div>
  );
}
